import React, { useState } from "react";
import PropTypes from "prop-types";
import { Card, CardBody, Button, Input, Spinner } from "reactstrap";
import { Link } from "react-router-dom";
import { getAllSections, getInitials, COPYABLE_KEYS } from "../utils/clientDetailsHelpers";

const copyToClipboard = (value) => {
  try {
    navigator.clipboard.writeText(value);
  } catch (e) {
    // Clipboard access can be denied (permissions, non-HTTPS context) — not
    // worth surfacing an error for a convenience action.
  }
};

// Read-only Identity/Contact/Address/Banking tabs (same field configs the
// create/edit wizard uses) plus the "Linked client" footer bar. Editing
// happens exclusively through ClientModal (mode="edit"), opened via onEdit.
const ClientDetailsCard = ({
  clientInfo,
  isNaturalClient,
  isViewer,
  onEdit,
  additionalContact,
  availableContacts,
  isContactOperationPending,
  onLinkExisting,
  onUnlinkRequest,
}) => {
  const sections = getAllSections(isNaturalClient);
  const [tab, setTab] = useState(sections[0].title);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [pickedContactId, setPickedContactId] = useState("");

  const activeSection = sections.find((s) => s.title === tab) || sections[0];
  const totalRows = sections.reduce((a, s) => a + s.fields.length, 0);
  const filledRows = sections.reduce(
    (a, s) => a + s.fields.filter((f) => !!clientInfo?.[f.key]).length,
    0
  );
  const pct = totalRows ? Math.round((filledRows / totalRows) * 100) : 0;
  const pctColor = pct > 80 ? "#146c43" : "#8a6b12";

  return (
    <Card style={{ border: "none", boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.15)", borderRadius: "14px" }} className="mb-4">
      <div className="d-flex align-items-center gap-2 px-3 py-3 border-bottom">
        <h5 className="mb-0 flex-grow-1">Client details</h5>
        <span style={{ fontSize: 12, fontWeight: 600, color: pctColor }}>{pct}% complete</span>
        {!isViewer && (
          <Button size="sm" color="light" onClick={onEdit}>
            Edit
          </Button>
        )}
      </div>
      <div style={{ height: 3, background: "#f0f0f4" }}>
        <div
          style={{ height: "100%", width: `${pct}%`, background: pct > 80 ? "#1a9c5f" : "#e0b23c" }}
        />
      </div>

      <div className="d-flex gap-1 px-3 pt-2 flex-wrap">
        {sections.map((s) => {
          const missing = s.fields.filter((f) => !clientInfo?.[f.key]).length;
          const on = s.title === tab;
          return (
            <button
              key={s.title}
              type="button"
              onClick={() => setTab(s.title)}
              className="btn btn-sm d-flex align-items-center gap-2"
              style={{
                background: "transparent",
                border: "none",
                borderBottom: `2px solid ${on ? "var(--vz-primary)" : "transparent"}`,
                borderRadius: 0,
                color: on ? "#1b1b21" : "#6f6f7c",
                fontWeight: 600,
              }}
            >
              {s.title}
              <span
                className="rounded-pill"
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  padding: "1px 7px",
                  background: missing ? "#fdf7e7" : "#f2f2f6",
                  color: missing ? "#8a6b12" : "#63636f",
                }}
              >
                {missing ? `${missing} left` : s.fields.length}
              </span>
            </button>
          );
        })}
      </div>
      <hr className="my-0" />

      <CardBody
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "14px 18px",
        }}
      >
        {activeSection.fields.map((f) => {
          const value = clientInfo?.[f.key];
          const copyable = COPYABLE_KEYS.includes(f.key) && !!value;
          return (
            <div key={f.key} style={{ minWidth: 0, gridColumn: f.wide ? "1 / -1" : "auto" }}>
              <div className="d-flex align-items-center gap-1">
                <span
                  className="text-uppercase"
                  style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.5, color: "#6f6f7c" }}
                >
                  {f.label}
                </span>
                {copyable && (
                  <button
                    type="button"
                    onClick={() => copyToClipboard(value)}
                    title="Copy"
                    className="btn btn-sm p-0 border-0"
                    style={{ fontSize: 10, lineHeight: 1, color: "#8a8a96" }}
                  >
                    <i className="ri-file-copy-line" />
                  </button>
                )}
              </div>
              <div
                style={{
                  fontSize: 13.5,
                  fontWeight: 500,
                  marginTop: 3,
                  overflowWrap: "anywhere",
                  fontVariantNumeric: "tabular-nums",
                  color: value ? "#1b1b21" : "#767683",
                }}
              >
                {value || "Not set"}
              </div>
            </div>
          );
        })}
      </CardBody>

      {isNaturalClient && (
        <div
          className="border-top d-flex flex-wrap align-items-center gap-2 px-3 py-3"
          style={{ background: "var(--vz-light)", borderBottomLeftRadius: 14, borderBottomRightRadius: 14 }}
        >
          <span
            className="text-uppercase text-muted flex-shrink-0"
            style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.9 }}
          >
            Linked client
          </span>

          {additionalContact ? (
            <>
              <Link
                to={`/client-details/${additionalContact.id}`}
                className="d-flex align-items-center gap-2 border rounded-3 px-2 py-1 bg-white text-body text-decoration-none flex-grow-1"
                style={{ minWidth: 220 }}
              >
                <span
                  className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ width: 30, height: 30, background: "#eceafd", color: "#4338ca", fontSize: 11, fontWeight: 700 }}
                >
                  {getInitials(additionalContact.display_name)}
                </span>
                <span className="flex-grow-1" style={{ minWidth: 0 }}>
                  <span className="d-block fw-semibold text-truncate" style={{ fontSize: 13.5 }}>
                    {additionalContact.display_name}
                  </span>
                  <span className="d-block text-muted text-truncate" style={{ fontSize: 11.5 }}>
                    Secondary client
                  </span>
                </span>
              </Link>
              {!isViewer && (
                <Button size="sm" color="outline-danger" onClick={onUnlinkRequest} disabled={isContactOperationPending}>
                  {isContactOperationPending ? <Spinner size="sm" /> : "Unlink"}
                </Button>
              )}
            </>
          ) : !isViewer ? (
            linkPickerOpen ? (
              <div className="d-flex gap-2 flex-grow-1 flex-wrap">
                <Input
                  type="select"
                  value={pickedContactId}
                  onChange={(e) => setPickedContactId(e.target.value)}
                  style={{ maxWidth: 260 }}
                >
                  <option value="">Select a contact</option>
                  {(availableContacts || []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.display_name}
                    </option>
                  ))}
                </Input>
                <Button
                  size="sm"
                  color="success"
                  disabled={!pickedContactId || isContactOperationPending}
                  onClick={() => {
                    onLinkExisting(pickedContactId);
                    setLinkPickerOpen(false);
                    setPickedContactId("");
                  }}
                >
                  {isContactOperationPending ? <Spinner size="sm" /> : "Link"}
                </Button>
                <Button size="sm" color="light" onClick={() => setLinkPickerOpen(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setLinkPickerOpen(true)}
                className="btn btn-sm btn-outline-primary flex-grow-1"
                style={{ borderStyle: "dashed", minWidth: 220 }}
              >
                + Link a secondary client
              </button>
            )
          ) : (
            <span className="text-muted" style={{ fontSize: 12 }}>
              None linked
            </span>
          )}
        </div>
      )}
    </Card>
  );
};

ClientDetailsCard.propTypes = {
  clientInfo: PropTypes.object,
  isNaturalClient: PropTypes.bool,
  isViewer: PropTypes.bool,
  onEdit: PropTypes.func,
  additionalContact: PropTypes.object,
  availableContacts: PropTypes.array,
  isContactOperationPending: PropTypes.bool,
  onLinkExisting: PropTypes.func,
  onUnlinkRequest: PropTypes.func,
};

export default ClientDetailsCard;
